import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { ref, push, onValue, remove, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

const AddGallery = () => {
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState('Adventure');
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadMethod, setUploadMethod] = useState('file'); // 'file' or 'url'
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [galleryItems, setGalleryItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);

  const categories = ['Adventure', 'Cultural', 'Nature', 'Education', 'History', 'Urban', 'Activities'];

  // Fetch gallery items on component mount
  useEffect(() => {
    const galleryRef = ref(db, 'gallery');
    const unsubscribe = onValue(galleryRef, (snapshot) => {
      const items = [];
      snapshot.forEach((childSnapshot) => {
        items.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      setGalleryItems(items);
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (uploadMethod === 'file' && !imageFile && !editingItem) {
      setErrorMessage('Please select an image file');
      return;
    }

    if (uploadMethod === 'url' && !imageUrl && !editingItem) {
      setErrorMessage('Please provide an image URL');
      return;
    }

    setIsUploading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      let finalImageUrl = imageUrl;

      // If uploading a file, handle the file upload
      if (uploadMethod === 'file' && imageFile) {
        const fileRef = storageRef(storage, `gallery/${uuidv4()}`);
        await uploadBytes(fileRef, imageFile);
        finalImageUrl = await getDownloadURL(fileRef);
      }

      if (editingItem) {
        // Update existing item
        const itemRef = ref(db, `gallery/${editingItem.id}`);
        await update(itemRef, {
          caption,
          category,
          imageUrl: finalImageUrl || editingItem.imageUrl,
          createdAt: editingItem.createdAt
        });
        setSuccessMessage('Gallery item updated successfully!');
      } else {
        // Save new gallery item to Realtime Database
        const galleryRef = ref(db, 'gallery');
        await push(galleryRef, {
          caption,
          category,
          imageUrl: finalImageUrl,
          createdAt: new Date().toISOString()
        });
        setSuccessMessage('Gallery item added successfully!');
      }

      // Reset form
      setCaption('');
      setCategory('Adventure');
      setImageFile(null);
      setImageUrl('');
      setEditingItem(null);
    } catch (error) {
      console.error('Error adding/updating gallery item:', error);
      setErrorMessage('Failed to add/update gallery item. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setCaption(item.caption);
    setCategory(item.category);
    setImageUrl(item.imageUrl);
    setUploadMethod(item.imageUrl.startsWith('http') ? 'url' : 'file');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (item) => {
    if (window.confirm('Are you sure you want to delete this gallery item?')) {
      try {
        // Delete from database
        const itemRef = ref(db, `gallery/${item.id}`);
        await remove(itemRef);

        // If the image is stored in Firebase Storage, delete it
        if (item.imageUrl.includes('firebasestorage.googleapis.com')) {
          const fileRef = storageRef(storage, item.imageUrl);
          await deleteObject(fileRef).catch(error => {
            console.log('Error deleting storage file:', error);
          });
        }

        setSuccessMessage('Gallery item deleted successfully!');
      } catch (error) {
        console.error('Error deleting gallery item:', error);
        setErrorMessage('Failed to delete gallery item. Please try again.');
      }
    }
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setCaption('');
    setCategory('Adventure');
    setImageFile(null);
    setImageUrl('');
    setUploadMethod('file');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>
        {editingItem ? 'Edit Gallery Item' : 'Add Gallery Item'}
      </h2>
      
      <form onSubmit={handleSubmit} style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '20px',
        marginBottom: '40px',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Upload Method</label>
          <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <input
                type="radio"
                name="uploadMethod"
                value="file"
                checked={uploadMethod === 'file'}
                onChange={() => setUploadMethod('file')}
              />
              Upload File
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <input
                type="radio"
                name="uploadMethod"
                value="url"
                checked={uploadMethod === 'url'}
                onChange={() => setUploadMethod('url')}
              />
              Image URL
            </label>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Caption</label>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '6px',
              border: '1px solid #ddd'
            }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '6px',
              border: '1px solid #ddd'
            }}
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        {uploadMethod === 'file' ? (
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Image File {editingItem && <span>(Leave empty to keep current image)</span>}
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
              required={!editingItem && uploadMethod === 'file'}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #ddd'
              }}
            />
          </div>
        ) : (
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Image URL</label>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              required={!editingItem && uploadMethod === 'url'}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #ddd'
              }}
            />
          </div>
        )}
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="submit"
            disabled={isUploading}
            style={{
              padding: '12px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'background-color 0.3s',
              opacity: isUploading ? 0.7 : 1,
              flex: 1
            }}
          >
            {isUploading ? (editingItem ? 'Updating...' : 'Uploading...') : (editingItem ? 'Update Item' : 'Add to Gallery')}
          </button>
          
          {editingItem && (
            <button
              type="button"
              onClick={cancelEdit}
              style={{
                padding: '12px',
                backgroundColor: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'background-color 0.3s',
                flex: 1
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
      
      {successMessage && (
        <div style={{ 
          marginTop: '20px', 
          padding: '10px', 
          backgroundColor: '#d4edda', 
          color: '#155724',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div style={{ 
          marginTop: '20px', 
          padding: '10px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          {errorMessage}
        </div>
      )}
      
      <h2 style={{ textAlign: 'center', margin: '40px 0 20px' }}>Gallery Items</h2>
      
      {galleryItems.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#777' }}>No gallery items found. Add some using the form above.</p>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '20px',
          padding: '10px'
        }}>
          {galleryItems.map((item) => (
            <div key={item.id} style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
              position: 'relative'
            }}>
              <img 
                src={item.imageUrl} 
                alt={item.caption}
                style={{
                  width: '100%',
                  height: '200px',
                  objectFit: 'cover'
                }}
              />
              <div style={{ padding: '15px' }}>
                <h3 style={{ margin: '0 0 5px', fontSize: '18px' }}>{item.caption}</h3>
                <p style={{ 
                  margin: '0 0 10px',
                  color: '#555',
                  fontSize: '14px'
                }}>
                  Category: {item.category}
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleEdit(item)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#3498db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      flex: 1
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      flex: 1
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddGallery;